import { useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Box, Link } from '@mui/material';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Logo from 'Components/Logo';
import { MainAppReducer, PlayInput } from 'Types';
import { getPlatformInfo } from 'Actions/events';
import { usePlayInputChange } from 'Components/PlayAlong/usePlayInputChange';
import { reportEventToGoogleAnalytics } from 'Utils/GoogleTags';
import {
  KEYBOARD_BUYING_FAQ_URL,
  SAVE_FAILED_MESSAGE,
  accountStoresPlayInput,
} from 'Utils/playAlong/playInputAccount';
import KeyboardConnectMidi from '../../assets/images/KeyboardConnectMidi.png';
import MidiKeyboard from '../../assets/images/MidiKeyboard.png';
import MidiKeyboardSound from '../../assets/images/MidiKeyboardSound.png';
import WelcomeButton from './WelcomeButton';
import './trial_welcome.css';
import './welcome.css';

interface DeviceCard {
  value: PlayInput;
  title: string;
  description: ReactNode;
  image: ReactNode;
}

const DEVICE_HELP_LINE =
  "Not sure? If it plugs into your computer or tablet with a USB cable, or pairs by Bluetooth, it's MIDI.";

const DEVICE_CARDS: readonly DeviceCard[] = [
  {
    value: 'midi',
    title: 'A MIDI keyboard',
    description: 'MuseFlow hears every note and grades your playing.',
    image: <img src={KeyboardConnectMidi} alt="" />,
  },
  {
    value: 'unconnected_piano',
    title: 'A piano or keyboard without MIDI',
    description:
      "An acoustic piano, or a keyboard that doesn't connect. Play along with the music; nothing is graded.",
    image: <img src={MidiKeyboardSound} alt="" />,
  },
  {
    value: 'none',
    title: 'Nothing yet, just exploring',
    description: (
      <>
        Look around and play along. Thinking about a keyboard?{' '}
        <Link
          href={KEYBOARD_BUYING_FAQ_URL}
          target="_blank"
          rel="noopener"
          className="link"
          onClick={(event) => event.stopPropagation()}
        >
          See what to get.
        </Link>
      </>
    ),
    // A keyboard not there yet, and a compass for looking around.
    image: (
      <Box className="device-setup-explore-image">
        <img src={MidiKeyboard} alt="" />
        <ExploreOutlined className="device-setup-explore-icon" />
      </Box>
    ),
  },
];

/**
 * After the welcome screen: "What will you play on?". The answer sets how the
 * player plays (a MIDI keyboard is heard and graded; anything else plays along,
 * ungraded) and ends the welcome flow. A player who leaves before answering is
 * brought back here.
 */
/** The answer, to GA4 (once per answer saved). */
const reportDeviceSetupChoice = (playInput: PlayInput) =>
  reportEventToGoogleAnalytics('device_setup_choice', {
    play_input: playInput,
    midi_compatible: playInput === 'midi',
    platform: getPlatformInfo(),
  });

const DeviceSetup = () => {
  const navigate = useNavigate();
  // The query a redirect here brought along (a payment's thank-you return)
  // goes on to the roadmap.
  const { search } = useLocation();
  const userData = useSelector((state: MainAppReducer) => state.mainAppReducer.userData);
  const changePlayInput = usePlayInputChange();
  const [selected, setSelected] = useState<PlayInput | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Nothing to ask: a backend that does not store the answer, or a player who
  // has answered already (reached again by Back or a link).
  if (userData && (!accountStoresPlayInput(userData) || userData.welcome_flow_setup_complete)) {
    return <Navigate to={`/roadmap${search}`} replace />;
  }

  const onSubmit = async () => {
    if (selected === null || saving) return;
    setSaving(true);
    setSaveFailed(false);
    // A save that ran out of time but landed after all is reported then; the
    // screen then sees the answer and moves on by itself.
    const saved = await changePlayInput(selected, 'device_screen', {
      onLateSave: reportDeviceSetupChoice,
    });
    setSaving(false);
    if (!saved) {
      setSaveFailed(true);
      return;
    }
    reportDeviceSetupChoice(selected);
    // Replace, so Back cannot return to a question already answered.
    navigate(`/roadmap${search}`, { replace: true });
  };

  const selectWithKey = (event: KeyboardEvent<HTMLDivElement>, value: PlayInput) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setSelected(value);
  };

  return (
    <Box className="page-div-styles">
      <Box className="filter-div-styles">
        <Box className="sign-in-div-styles device-setup">
          <Logo />
          <Box className="device-setup-body">
            <Box className="desc-container">
              <h3 id="device-setup-title" className="title-welcome device-setup-title">
                What will you play on?
              </h3>
              <p className="p-connect-midi">You can change this any time in Settings.</p>
            </Box>
            <Box
              role="radiogroup"
              aria-labelledby="device-setup-title"
              className="device-setup-cards"
            >
              {DEVICE_CARDS.map((card) => {
                const isSelected = selected === card.value;
                return (
                  <Box
                    key={card.value}
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={0}
                    data-play-input={card.value}
                    className={isSelected ? 'device-setup-card selected' : 'device-setup-card'}
                    onClick={() => setSelected(card.value)}
                    onKeyDown={(event) => selectWithKey(event, card.value)}
                  >
                    <Box className="device-setup-card-image" aria-hidden>
                      {card.image}
                    </Box>
                    <Box className="device-setup-card-text">
                      <p className="device-setup-card-title">{card.title}</p>
                      <p className="device-setup-card-description">{card.description}</p>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <p className="device-setup-help">{DEVICE_HELP_LINE}</p>
          </Box>
          <Box className="connect-button-container">
            {saveFailed && (
              <p role="alert" className="device-setup-error">
                {SAVE_FAILED_MESSAGE}
              </p>
            )}
            <WelcomeButton
              onClick={() => void onSubmit()}
              style={{ backgroundColor: selected === null ? '#767C8C' : '#CA7C62' }}
              disabled={selected === null || saving}
            >
              {"LET'S FLOW"}
            </WelcomeButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DeviceSetup;
